'use client'
import { setProductFilter } from '@/app/globalRedux/filter/productFilter'
import { RootState } from '@/app/globalRedux/store'
import { AdBannerScheduleType } from '@/app/types/adType'
import { ProductSearchResponse } from '@/app/types/productSearchType'
import LimitPagination from '@/components/LimitPagination'
import { convertNaNToOne } from '@/lib/formatUtils'
import {
  useParams,
  usePathname,
  useRouter,
  useSearchParams,
} from 'next/navigation'
import React, { useEffect, useOptimistic, useState, useTransition } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import InBetweenAds from '../(AdComponent)/InBetweenAds'
import HomeSalesOfferSkeleton from '../../../components/Skeletons/HomeSalesOfferSkeleton'
import CustomErrorBoundary from '../../../ErrorBoundary'
import EmptyResult from '../EmptyResult'
import AlertCard from './(components)/AlertCard'
import OfferCard from './(components)/OfferCard'

const adInterval = 3

const ProductsList = ({
  adList,
  productData,
  isLoading,
}: {
  adList?: AdBannerScheduleType[]
  productData: ProductSearchResponse
  isLoading: boolean
}) => {
  const dispatch = useDispatch()
  const pathName = usePathname()
  const params = useParams()
  const searchParams = useSearchParams()
  const [adsData, setAdsData] = useState<AdBannerScheduleType[]>([])
  const productFilter = useSelector((state: RootState) => state.productFilter)
  const router = useRouter()
  const [isPending, startTransition] = useTransition()
  const [optimisticPageNo, setOptimisticPageNo] = useOptimistic<number>(
    convertNaNToOne(Number(params?.productPageNO?.[0]) || 1)
  )

  const handlePageChange = (page: number) => {
    startTransition(() => {
      setOptimisticPageNo(page)
      if (pathName.includes('buyers-listing')) {
        router.push(`/${params.lng}/buyers-listing/` + page)
      } else if (pathName.includes('sales-listing')) {
        router.push(`/${params.lng}/sales-listing/` + page)
      } else if (pathName.includes('product')) {
        router.push(
          `/${params.lng}/search/product-tab/` +
            page +
            `?searchTerm=${searchParams.get('searchTerm') ?? ''}`
        )
      }
    })
  }

  const isProductLoading = () => {
    return (
      isLoading ||
      isPending ||
      (!productData?.products?.length &&
        Number(params?.productPageNO?.[0]) !== 1)
    )
  }

  useEffect(() => {
    if (adList) {
      const adFilter5 =
        adList.filter((ad) => ad.ad_position.name === 'Ad Position 5')[0] ?? {}
      const adFilter6 =
        adList.filter((ad) => ad.ad_position.name === 'Ad Position 6')[0] ?? {}
      const adFilter7 =
        adList.filter((ad) => ad.ad_position.name === 'Ad Position 7')[0] ?? {}

      const adsData = [adFilter5, adFilter6, adFilter7]

      setAdsData(adsData)
    }
  }, [adList])

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [pathName])

  useEffect(() => {
    if (productData?.products?.length == 0 && !isLoading) {
      handlePageChange(1)
    }
  }, [productData])

  useEffect(() => {
    dispatch(setProductFilter({ isRouteUpdating: isPending }))
  }, [isPending])

  return (
    <>
      {isProductLoading() ? (
        <div className=" gap-2 flex flex-col">
          {Array.from({ length: 14 }, (_, index) => (
            <HomeSalesOfferSkeleton key={index + 'product_listing'} />
          ))}
        </div>
      ) : productData?.products?.length > 0 ? (
        <div className="flex flex-col gap-y-[10px]">
          {productData?.products?.map((item, index) => (
            <React.Fragment key={index}>
              {'alert' in item && (
                <CustomErrorBoundary key={`${item?.alert?.id}_alert`}>
                  <AlertCard
                    key={`buyer_${item?.alert?.id}_${index}`}
                    cardData={item?.alert}
                  />
                </CustomErrorBoundary>
              )}

              {'offer' in item && (
                <CustomErrorBoundary key={`${item?.offer?.id}_offer`}>
                  <OfferCard
                    key={`sales_${item?.offer?.id}_${index}`}
                    cardData={item?.offer}
                  />
                </CustomErrorBoundary>
              )}

              {(index + 1) % adInterval === 0 && (
                <InBetweenAds
                  index={index}
                  adsData={
                    adsData[Math.floor(index / adInterval) % adsData.length]
                  }
                />
              )}
            </React.Fragment>
          ))}
        </div>
      ) : (
        <EmptyResult searchTerm={productFilter?.searchTerm ?? ''} />
      )}

      {productData?.products?.length > 0 && (
        <div className="mt-[35px] border-t-2 border-t-[#e6e6e6] border-solid mobile:mt-[10px]">
          <LimitPagination
            currentPage={optimisticPageNo}
            pageCount={Math.ceil(productData?.count / 10) || 1}
            visiblePages={5}
            onPageChange={(page) => {
              handlePageChange(page)
            }}
          />
        </div>
      )}
    </>
  )
}

export default ProductsList
